import React, { useState, useEffect, useRef } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { createMediaShare, MediaShareResponse } from "@/services/mediaShareService";
import { createCollectionShare } from "@/services/collectionsService";
import { useJobSSE } from "@/hooks/useJobSSE";
import { formatFileSize, formatExpiryDate } from "./utils/shareFormatters";
import { copyToClipboard } from "@/utils/clipboard";

interface MediaShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds?: number[];
  collectionId?: string;
  clientId: string;
}

type ModalState = "idle" | "creating" | "processing" | "ready" | "error";

// Shape of result_data from the media_share job
interface ShareJobResult {
  fileCount?: number;
  totalSizeBytes?: number;
}

export const MediaShareModal: React.FC<MediaShareModalProps> = ({
  isOpen,
  onClose,
  selectedIds = [],
  collectionId,
  clientId,
}) => {
  const [state, setState] = useState<ModalState>("idle");
  const [shareData, setShareData] = useState<MediaShareResponse | null>(null);
  const [jobResult, setJobResult] = useState<ShareJobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  // Subscribe to job updates via SSE (replaces polling)
  const { job, isCompleted, isFailed, isCancelled, progress, progressMessage } = useJobSSE(jobId);

  // Track if we've already processed this job's terminal state
  const processedJobIdRef = useRef<string | null>(null);

  // React to job completion/failure/cancellation via SSE
  useEffect(() => {
    if (!job || !jobId) return;

    // Prevent processing the same job status multiple times
    if (processedJobIdRef.current === jobId) return;

    if (isCompleted) {
      processedJobIdRef.current = jobId;
      const result = job.resultData as ShareJobResult | null;
      setJobResult(result);
      setState("ready");
    } else if (isFailed || isCancelled) {
      processedJobIdRef.current = jobId;
      setError(job.errorMessage || (isCancelled ? "Share creation cancelled" : "Job failed"));
      setState("error");
    }
  }, [job, jobId, isCompleted, isFailed, isCancelled]);

  const createShare = async () => {
    setState("creating");
    setError(null);
    setJobId(null);
    processedJobIdRef.current = null;

    try {
      // Use collection share if collectionId is provided, otherwise use direct media share
      const response = collectionId
        ? await createCollectionShare(clientId, collectionId)
        : await createMediaShare(clientId, selectedIds);
      setShareData(response);

      if (response.status === "ready") {
        // Synchronous processing completed (local dev)
        setState("ready");
      } else if (response.status === "creating" && response.jobId) {
        // Async processing via Cloud Run Job - SSE will push updates
        setState("processing");
        setJobId(response.jobId);
      } else if (response.status === "failed") {
        setError("Failed to create share");
        setState("error");
      }
    } catch (err: unknown) {
      const axiosError = err as { message?: string };
      setError(axiosError.message || "Failed to create share link");
      setState("error");
    }
  };

  // Start creating share when modal opens
  useEffect(() => {
    const hasContent = collectionId || selectedIds.length > 0;
    if (isOpen && state === "idle" && hasContent) {
      createShare();
    }
  }, [isOpen]);

  // Reset state when modal closes
  const handleClose = () => {
    setState("idle");
    setShareData(null);
    setJobResult(null);
    setJobId(null);
    processedJobIdRef.current = null;
    setError(null);
    setCopiedField(null);
    onClose();
  };

  const handleCopy = async (text: string, field: string) => {
    await copyToClipboard(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Use job result if available, otherwise fall back to initial response
  const displayFileCount = jobResult?.fileCount ?? shareData?.fileCount ?? 0;
  const displayTotalSize = jobResult?.totalSizeBytes ?? shareData?.totalSizeBytes ?? 0;
  const displayExpiresAt = shareData?.expiresAt;

  // Ensure we have a full URL for copying/display
  const getFullUrl = (url: string): string => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    // Construct full URL from current origin
    return `${window.location.origin}${url}`;
  };

  const fullDownloadUrl = shareData?.downloadUrl ? getFullUrl(shareData.downloadUrl) : "";

  const footerContent = (
    <Button onClick={handleClose} variant="ghost">
      Close
    </Button>
  );

  const modalTitle = collectionId ? "Share Collection" : "Share Media";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      size="lg"
      footer={footerContent}
    >
      <div className="p-6">
        <div className="space-y-6">
          {/* Creating state - brief initial request */}
          {state === "creating" && !shareData && (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="text-center">
                  <p className="text-gray-700 font-medium">Creating share link...</p>
                </div>
              </div>
            </div>
          )}

          {/* Error state */}
          {state === "error" && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-500 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <div className="text-red-800 font-medium text-sm">Failed to create share link</div>
                  <div className="text-red-700 text-sm mt-1">{error}</div>
                  <div className="mt-3">
                    <Button
                      onClick={createShare}
                      variant="ghost"
                      size="sm"
                      className="text-red-700 hover:text-red-800 hover:bg-red-100"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Link visible state - show as soon as we have shareData */}
          {shareData && state !== "error" && (
            <div className="space-y-6">
              {/* Status banner - different based on processing state */}
              {state === "ready" ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-900 text-sm">Share Link Created</h3>
                      <p className="text-green-700 text-sm mt-1">
                        {displayFileCount} file{displayFileCount !== 1 ? "s" : ""} ({formatFileSize(displayTotalSize)}) ready for download.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                      <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-blue-900 text-sm">Share Link Ready</h3>
                      <p className="text-blue-700 text-sm mt-1">
                        {progressMessage || "Download package is being prepared in the background."}
                      </p>
                      {progress > 0 && (
                        <div className="mt-2">
                          <div className="w-full bg-blue-200 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Download URL */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Download Link</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={fullDownloadUrl}
                    readOnly
                    className="flex-1 p-2 bg-gray-50 border border-gray-200 rounded-sm text-sm font-mono text-gray-600"
                  />
                  <button
                    onClick={() => handleCopy(fullDownloadUrl, "url")}
                    className={`px-3 py-2 text-xs font-medium rounded transition-colors ${
                      copiedField === "url"
                        ? "bg-green-100 text-green-700 border border-green-200"
                        : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {copiedField === "url" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Expiry info */}
              {displayExpiresAt && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-amber-600 mt-0.5 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div>
                      <h4 className="font-medium text-amber-900 text-sm">Link expires</h4>
                      <p className="text-amber-700 text-sm mt-1">{formatExpiryDate(displayExpiresAt)}</p>
                      <p className="text-amber-600 text-xs mt-1">
                        Anyone with this link can download the files until it expires.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
